/**
 * Tag rule SCOPE — the point of view the rules list is read and edited from.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Two nested choices: a country, then optionally one campaign inside it. That
 * mirrors how the thresholds actually resolve (campaign → country → default),
 * so what you select IS the precedence chain rather than a parallel idea.
 *
 * Empty = the plain default view. A campaign without a country is not a state
 * the UI can produce, but it stays readable if one arrives in a URL.
 */

import { parseTrackerEnum, type TagTrafficProvider } from '@trafficpulse/shared';

export interface TagRuleScope {
  countryKey?: string;
  provider?: TagTrafficProvider;
  campaignId?: string;
}

export const DEFAULT_SCOPE: TagRuleScope = {};

/** Ids compare case-insensitively — Voluum stores UUIDs lowercase but a pasted
 *  id is often upper, and country keys arrive in mixed case. */
export const normScope = (v: string): string => v.trim().toLowerCase();

/** Which level a write lands on: the most specific thing selected. */
export type ScopeTarget = 'default' | 'country' | 'campaign';

export function scopeTarget(scope: TagRuleScope | undefined): ScopeTarget {
  if (scope?.provider && scope.campaignId?.trim()) return 'campaign';
  if (scope?.countryKey?.trim()) return 'country';
  return 'default';
}

export function isDefaultScope(scope: TagRuleScope | undefined): boolean {
  return scopeTarget(scope) === 'default';
}

export function scopeCampaignKey(scope: TagRuleScope): string {
  if (!scope.provider || !scope.campaignId) return '';
  return `${scope.provider}:${normScope(scope.campaignId)}`;
}

/** Stable identity for React keys and equality checks. */
export function scopeKey(scope: TagRuleScope): string {
  const country = scope.countryKey ? normScope(scope.countryKey) : '';
  return `${country}|${scopeCampaignKey(scope)}`;
}

export function sameScope(a: TagRuleScope, b: TagRuleScope): boolean {
  return scopeKey(a) === scopeKey(b);
}

/** Parse a `PROVIDER:id` campaign token; anything malformed yields {}. */
export function parseCampaignToken(token: string | null | undefined): TagRuleScope {
  const [rawProvider, ...rest] = (token ?? '').trim().split(':');
  const campaignId = rest.join(':').trim();
  const provider = parseTrackerEnum(rawProvider);
  if (provider && campaignId) {
    return { provider, campaignId };
  }
  return {};
}

/** Build a scope from the two URL params. Unrecognised values degrade to
 *  default rather than throwing, so a stale link still opens. */
export function scopeFromParams(
  country: string | null | undefined,
  campaign: string | null | undefined,
): TagRuleScope {
  const countryKey = (country ?? '').trim();
  const camp = parseCampaignToken(campaign);
  // A campaign is only meaningful inside its country, which is how it is picked.
  if (!countryKey) return {};
  return { countryKey, ...camp };
}
